<template>
  <b-modal
    id="viewFile"
    v-model="isVisible"
    size="xl"
    centered
    hide-footer
    @hidden="onHide"
    dialog-class="my-0"
    content-class="p-0 overflow-hidden"
    body-class="p-0 d-flex align-items-center justify-content-center"
  >
    <div class="w-100 h-100 d-flex align-items-center justify-content-center" ref="wrapper">
      <!-- Imagen (png/jpg/webp/gif o preview DICOM) -->
      <b-img
        v-if="isImage"
        :src="resizedImageUrl || urlFileToView" 
        alt="Vista previa de archivo"
        class="preview-image"
      />

      <!-- PDF: usar <object> para mayor compatibilidad con data:application/pdf y rutas directas -->
      <object
        v-else-if="isPdf"
        :data="urlFileToView"
        type="application/pdf"
        class="w-100"
        style="height:60vh;"
      >
        <iframe :src="urlFileToView" class="w-100" style="height:60vh;border:none;"></iframe>
      </object>

      <!-- Otros (txt, doc, etc.) -->
      <iframe
        v-else
        :src="urlFileToView"
        class="w-100"
        style="height:90vh;border:none;"
      ></iframe>
    </div>
  </b-modal>
</template>

<script>
import rutas from '@/assets/utils/rutas.json';

export default {
  name: 'ModalViewFile',
  props: {
    urlFile: { type: String, default: null },
    value:   { type: Boolean, default: false },
    rutaCompleta: { type: Boolean, default: false },
  },
  data() {
    return {
      isVisible: this.value,
      resizedImageUrl: null,  // 👈 aquí guardamos la versión reducida real
    };
  },
  computed: {
    urlFileToView() {
      return this.rutaCompleta ? this.urlFile : `${rutas.axios}file/show${this.urlFile}`;
    },
    // Detecta el preview DICOM aunque no tenga extensión (.png)
    isDicomPreview() {
      const u = (this.urlFileToView || '').toLowerCase();
      return /\/(file\/show\/)?ver-dicom\//.test(u);
    },
    // Considera imagen si es jpg/png O si es el endpoint de preview DICOM
    isImage() {
      const u = (this.urlFileToView || this.urlFile || '').toLowerCase();
      return /\.(png|jpe?g|webp|gif)(\?|#|$)/.test(u) || this.isDicomPreview;
    },
    // PDF tanto por extension como por data URL
    isPdf() {
      const u = (this.urlFileToView || this.urlFile || '').toLowerCase();
      return /\.pdf(\?|#|$)/.test(u) || u.startsWith('data:application/pdf');
    },
  },
  watch: {
    value(v) {
      this.isVisible = v;
      if (v) this.prepareResized();
    },
    urlFileToView() {
      if (this.isVisible) this.prepareResized();
    },
  },
  mounted() {
    if (this.isVisible) this.prepareResized();
  },
  methods: {
    onHide() {
      this.$emit('input', false);
      this.resizedImageUrl = null;
    },

    async prepareResized() {
      this.resizedImageUrl = null;
      if (!this.isImage || !this.urlFileToView) return;

      await this.$nextTick();
      const wrap = this.$refs.wrapper;

      // Define aquí el “techo” de tu preview
      // Ejemplo: a la mitad del ancho del modal y 60vh de alto máximo
      const maxW = Math.floor((wrap?.clientWidth || window.innerWidth) * 0.5); // 50% del ancho del modal
      const maxH = Math.floor(window.innerHeight * 0.60);                      // 60% de la pantalla de alto

      try {
        this.resizedImageUrl = await this.createResizedImageUrl(this.urlFileToView, maxW, maxH, 'image/jpeg', 0.9);
      } catch (e) {
        console.warn('No se pudo redimensionar la imagen, uso original', e);
        this.resizedImageUrl = null;
      }
    },

    // Devuelve una DataURL de la imagen reducida para caber en maxW x maxH
    createResizedImageUrl(src, maxW, maxH, mime = 'image/jpeg', quality = 0.9) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        // mismo origen: no hace falta crossOrigin; si usas dominios distintos, habilita CORS y descomenta:
        // img.crossOrigin = 'anonymous';
        img.onload = () => {
          const iw = img.naturalWidth || img.width;
          const ih = img.naturalHeight || img.height;

          const scale = Math.min(maxW / iw, maxH / ih, 1); // no hacemos upscale
          const cw = Math.max(1, Math.floor(iw * scale));
          const ch = Math.max(1, Math.floor(ih * scale));

          const canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext('2d');

          // Progressive downscale para muy grandes
          if (scale < 0.5) {
            let oc = document.createElement('canvas');
            let octx = oc.getContext('2d');
            oc.width = Math.floor(iw * 0.5);
            oc.height = Math.floor(ih * 0.5);
            octx.drawImage(img, 0, 0, oc.width, oc.height);

            let curW = oc.width, curH = oc.height;
            while (curW * 0.5 > cw) {
              const oc2 = document.createElement('canvas');
              const octx2 = oc2.getContext('2d');
              oc2.width = Math.floor(curW * 0.5);
              oc2.height = Math.floor(curH * 0.5);
              octx2.drawImage(oc, 0, 0, oc2.width, oc2.height);
              oc = oc2; octx = octx2;
              curW = oc.width; curH = oc.height;
            }
            ctx.drawImage(oc, 0, 0, cw, ch);
          } else {
            ctx.drawImage(img, 0, 0, cw, ch);
          }

          const out = canvas.toDataURL(mime, quality);
          resolve(out);
        };
        img.onerror = reject;
        img.src = src;
      });
    },
  },
};
</script>
<style scoped>

:deep(.modal-xxl) {
  max-width: 95vw;   /* “xxl” a tu gusto */
  margin: 0 auto;
}
:deep(.modal-content) {
  display: flex;
  height: 95vh !important;
  flex-direction: column;
  max-height: 95vh;
  overflow: hidden;
}
:deep(.modal-body) {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  height: 100% !important;
  overflow: hidden !important;
}
.modal-content-wrapper {
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-image {
  display: block;
  max-width: 50%;   /* ocupa la mitad del modal */
  max-height: 60vh; /* techo de altura */
  margin: 0 auto;   /* centrado horizontal */
  object-fit: contain;
}
.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

</style>
